import { useEffect, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Copy, Database, KeyRound, Plug } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import * as api from "@/lib/api";
import { queryKeys, useSettings } from "@/lib/queries";

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "Something went wrong";
}

export default function SettingsPage() {
  const { data: settings, isLoading } = useSettings();
  const queryClient = useQueryClient();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [retentionDays, setRetentionDays] = useState("");
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [mcpBusy, setMcpBusy] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [retentionInitialized, setRetentionInitialized] = useState(false);

  useEffect(() => {
    if (settings && !retentionInitialized) {
      setRetentionDays(String(settings.retentionDays));
      setRetentionInitialized(true);
    }
  }, [retentionInitialized, settings]);

  async function changePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }
    setPasswordError("");
    setPasswordBusy(true);
    try {
      await api.changePassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Other sessions were signed out");
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setPasswordBusy(false);
    }
  }

  async function saveRetention(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const days = Number(retentionDays);
    if (!Number.isInteger(days) || days < 1 || days > 3650) {
      toast.error("Retention must be between 1 and 3650 days");
      return;
    }
    setSettingsBusy(true);
    try {
      await api.updateSettings({ retentionDays: days });
      await queryClient.invalidateQueries({ queryKey: queryKeys.settings });
      toast.success("Data retention updated");
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSettingsBusy(false);
    }
  }

  async function rotateMcpKey(successMessage: string) {
    setMcpBusy(true);
    try {
      await api.rotateMcpKey();
      await queryClient.invalidateQueries({ queryKey: queryKeys.settings });
      toast.success(successMessage);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setMcpBusy(false);
    }
  }

  async function disableMcp() {
    setMcpBusy(true);
    try {
      await api.disableMcp();
      await queryClient.invalidateQueries({ queryKey: queryKeys.settings });
      toast.success("MCP server disabled");
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setMcpBusy(false);
    }
  }

  async function copyToClipboard(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copied");
    } catch (error) {
      toast.error(errorMessage(error));
    }
  }

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-5xl mx-auto w-full">
      <PageHeader title="Settings" />
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader><div className="mb-2 flex size-10 items-center justify-center rounded-lg bg-muted"><KeyRound className="size-5 text-muted-foreground" /></div><CardTitle>Change password</CardTitle><CardDescription>Update the password for your administrator account.</CardDescription></CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={(event) => void changePassword(event)}>
              <div className="space-y-2"><Label htmlFor="current-password">Current password</Label><Input id="current-password" type="password" autoComplete="current-password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} required /></div>
              <div className="space-y-2"><Label htmlFor="new-password">New password</Label><Input id="new-password" type="password" autoComplete="new-password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} minLength={8} required /></div>
              <div className="space-y-2"><Label htmlFor="confirm-password">Confirm new password</Label><Input id="confirm-password" type="password" autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} minLength={8} required /></div>
              {passwordError && <p className="text-sm text-destructive">{passwordError}</p>}
              <Button type="submit" disabled={passwordBusy || !currentPassword || !newPassword || !confirmPassword}>{passwordBusy ? "Changing…" : "Change password"}</Button>
            </form>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><div className="mb-2 flex size-10 items-center justify-center rounded-lg bg-muted"><Database className="size-5 text-muted-foreground" /></div><CardTitle>Data retention</CardTitle><CardDescription>Control how long raw heartbeat history is retained.</CardDescription></CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-32" /> : (
              <form className="space-y-4" onSubmit={(event) => void saveRetention(event)}>
                <div className="space-y-2"><Label htmlFor="retention-days">Retention period (days)</Label><Input id="retention-days" type="number" min={1} max={3650} value={retentionDays} onChange={(event) => setRetentionDays(event.target.value)} className="max-w-40 tabular-nums" required /></div>
                <p className="text-sm leading-relaxed text-muted-foreground">Raw heartbeats older than this are deleted hourly; daily uptime stats are kept forever.</p>
                <Button type="submit" disabled={settingsBusy}>{settingsBusy ? "Saving…" : "Save"}</Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader><CardTitle>About</CardTitle><CardDescription>Vigil keeps watch over the services that matter.</CardDescription></CardHeader>
        <CardContent className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
          <span className="font-medium text-foreground">Vigil</span>
          <Badge variant="secondary">Bun runtime</Badge>
          <span className="tabular-nums">1.0.0</span>
          <span>Self-hosted uptime monitoring</span>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <div className="mb-2 flex size-10 items-center justify-center rounded-lg bg-muted">
            <Plug className="size-5 text-muted-foreground" />
          </div>
          <CardTitle>MCP Server</CardTitle>
          <CardDescription>
            Let AI assistants such as Claude and Cursor query and manage monitors through the Model Context Protocol.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading || !settings ? (
            <Skeleton className="h-32" />
          ) : settings.mcpApiKey === null ? (
            <div className="space-y-4">
              <p className="text-sm leading-relaxed text-muted-foreground">
                Generate an API key to connect an MCP-compatible AI assistant to Vigil.
              </p>
              <Button
                type="button"
                disabled={mcpBusy}
                onClick={() => void rotateMcpKey("MCP API key generated")}
              >
                {mcpBusy ? "Generating…" : "Generate API key"}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="mcp-endpoint">Endpoint</Label>
                <div className="flex gap-2">
                  <Input
                    id="mcp-endpoint"
                    value={`${window.location.origin}/mcp`}
                    readOnly
                    className="font-mono text-xs"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label="Copy MCP endpoint"
                    onClick={() => void copyToClipboard(`${window.location.origin}/mcp`)}
                  >
                    <Copy className="size-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="mcp-api-key">API key</Label>
                <div className="flex gap-2">
                  <Input
                    id="mcp-api-key"
                    value={settings.mcpApiKey}
                    readOnly
                    className="font-mono text-xs"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label="Copy MCP API key"
                    onClick={() => void copyToClipboard(settings.mcpApiKey ?? "")}
                  >
                    <Copy className="size-4" />
                  </Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={mcpBusy}
                  onClick={() => void rotateMcpKey("MCP API key rotated")}
                >
                  {mcpBusy ? "Working…" : "Rotate key"}
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={mcpBusy}
                  onClick={() => void disableMcp()}
                >
                  Disable
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
